import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import { currentViewportTier } from "@/hooks/useViewportTier";

interface SidebarContextType {
  /**
   * Whether the desktop panel is pinned open as a column (#12209). False is
   * the 64px rail; the hover reveal over the page is the sidebar's own
   * transient state and never lands here. Every consumer that "collapses"
   * the sidebar (a viewer opening, the RMS filters panel) unpins.
   */
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  /**
   * The pinned panel's width (#12209), dragged at its edge and remembered
   * (`sidebar-width`), between SIDEBAR_MIN_PX and SIDEBAR_MAX_PX. The rail
   * is always SIDEBAR_RAIL_PX. `persist: false` is the live drag, which
   * writes nothing until the pointer lifts.
   */
  sidebarWidth: number;
  setSidebarWidth: (width: number, persist?: boolean) => void;
  /** True while the edge is being dragged: width transitions stand down so
   *  the column follows the pointer rather than easing after it. */
  sidebarResizing: boolean;
  setSidebarResizing: (resizing: boolean) => void;
  /**
   * How many popups opened from the panel are open (#12209): an Owner menu,
   * a share or search modal. Their papers are portalled to the body, so to
   * the hover-open panel their focus and pointer look like a departure;
   * while one is open the reveal holds instead of closing. `holdReveal`
   * takes one and returns its release.
   */
  revealHolds: number;
  holdReveal: () => () => void;
  /**
   * The phone nav drawer (#11742): below 1024px the sidebar reserves no
   * column and instead presents as a scrim + overlay drawer, opened from
   * the topbars' menu button. Never persisted — a drawer is a moment, not
   * a preference.
   */
  mobileDrawerOpen: boolean;
  setMobileDrawerOpen: (open: boolean) => void;
  workspaceHistoryKey: string;
  setWorkspaceHistoryKey: (key: string) => void;
  /**
   * Whether Layout's document pane is on screen beside the page (#12332):
   * a page that keeps a side column of its own reads it to stand that
   * column down while a document shares the frame. Layout writes it.
   */
  documentPaneOpen: boolean;
  setDocumentPaneOpen: (open: boolean) => void;
  /**
   * Where Layout's document pane renders when a page offers a place for it
   * (#12481, Gerry: an artifact on the agent page "shouldn't take up the
   * nav bar"): a page with a bar of its own above a split row registers
   * the row's slot, and the pane lands there, under the bar, instead of
   * beside the whole page. Null means beside the page.
   */
  paneSlot: HTMLElement | null;
  setPaneSlot: (slot: HTMLElement | null) => void;
  /**
   * The pane's split as Layout holds it (#12481): the page share in
   * percent, and whether the divider is being dragged. A slot's host sizes
   * its own column from it, so the column eases to the pane's share on open
   * and back on close exactly as Layout's page column does.
   */
  paneSplit: PaneSplit;
  setPaneSplit: (split: PaneSplit) => void;
  /**
   * A page share the slot's host asks Layout to hold while a state of its
   * own lasts (#12481, Gerry: a file beside an artifact splits the row
   * evenly), in percent; null asks nothing, and Layout returns to the share
   * it had before.
   */
  paneSplitRequest: number | null;
  setPaneSplitRequest: (percent: number | null) => void;
  /**
   * Writes Layout's pane split (the page share, in percent; null for its
   * default), registered by Layout while it is mounted. A slot's host whose
   * own viewer card stands where Layout's pane does (the agent run page)
   * sizes that card from `paneSplit` and resizes it through this, so the
   * one viewer has one width whichever host draws it (Gerry, Oct 6: "the
   * width of the panel holding the file/artifact shouldn't change").
   */
  paneSplitWriter: ((percent: number | null) => void) | null;
  setPaneSplitWriter: (
    writer: ((percent: number | null) => void) | null
  ) => void;
  /**
   * The artifact Layout's pane is showing, as what its panel needs to draw
   * it (#12481, Gerry: "any file type whether two artifacts or anything
   * should be able to show"). A slot's host keeps a queue of open panes;
   * when the pane moves on to another artifact, the host takes the one it
   * left into that queue and draws it itself, so the two stand side by
   * side. Null for a pane that shows no such artifact: nothing, a document,
   * a live document, a record.
   */
  paneArtifact: PaneArtifact | null;
  setPaneArtifact: (artifact: PaneArtifact | null) => void;
  /**
   * One counter of "the reader opened or re-picked something" shared by
   * Layout's pane and a slot host's own panes (#12481), so Escape can tell
   * which of the two is the newer. `nextActivationMark` hands out the next
   * mark; `paneActivationRef` holds the mark Layout's pane took when what it
   * shows last changed (read at event time, never rendered from).
   */
  nextActivationMark: () => number;
  paneActivationRef: React.MutableRefObject<number>;
}

/** A table, text or diagram artifact, as its panel's props. */
export type PaneArtifact =
  | {
      kind: "table";
      artifactId?: string;
      editable?: boolean;
      title: string;
      content: string;
    }
  | {
      kind: "text";
      artifactId: string | null;
      editable?: boolean;
      title: string;
      filename: string;
      downloadUrl: string;
    }
  | {
      kind: "diagram";
      diagram: string;
      title: string;
      artifactId?: string;
      editable?: boolean;
    }
  // A live document (pane-tabs spec). Its editor holds one document's
  // state for the whole app, so only Layout's pane ever draws it: a host
  // keeps it as a tab and asks Layout to open it again when it is picked.
  | {
      kind: "editor";
      workspaceId: string;
      title: string;
      artifactId: string | null;
    };

export interface PaneSplit {
  percent: number;
  dragging: boolean;
}

const RESTING_SPLIT: PaneSplit = { percent: 50, dragging: false };

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

/**
 * The mark that tells Layout which of its children is the app's nav (#14170).
 * Layout lifts that child out of the page into its own column. It used to
 * look for a component whose function was named `Sidebar`, and the production
 * minifier renames functions: the nav then rendered as a block above the
 * page. A static on the component survives any build, so the shell asks for
 * the mark and never for a name.
 */
export const SHELL_SIDEBAR: unique symbol = Symbol("fp.shellSidebar");

export type ShellSidebarComponent<P extends object> = React.FC<P> & {
  readonly [SHELL_SIDEBAR]: true;
};

/** Marks `component` as the shell's nav, for Layout to place in its column. */
export function shellSidebar<P extends object>(
  component: React.FC<P>
): ShellSidebarComponent<P> {
  return Object.assign(component, { [SHELL_SIDEBAR]: true as const });
}

/** Whether an element's `type` is a component marked by `shellSidebar`. */
export function isShellSidebar(type: unknown): boolean {
  return (
    typeof type === "function" &&
    SHELL_SIDEBAR in type &&
    type[SHELL_SIDEBAR] === true
  );
}

const STORAGE_KEY = "sidebar-open";

/**
 * The desktop nav column's two widths (#12209): the rail at rest and the
 * pinned panel. Layout drives the column from these; the sidebar lays its
 * hover panel out at the pinned width; anything that centres on the content
 * column reads them rather than carrying its own copy.
 */
export const SIDEBAR_RAIL_PX = 64;
/** The pinned panel's default width; the stored width (below) overrides it. */
export const SIDEBAR_PINNED_PX = 248;
export const SIDEBAR_MIN_PX = 200;
export const SIDEBAR_MAX_PX = 420;
const WIDTH_STORAGE_KEY = "sidebar-width";

export function clampSidebarWidth(width: number): number {
  return Math.min(SIDEBAR_MAX_PX, Math.max(SIDEBAR_MIN_PX, Math.round(width)));
}

/** The remembered pinned width, or the default. Exported for the cold-load
 *  placeholder, which renders above the provider. */
export function readStoredSidebarWidth(): number {
  try {
    const stored = Number(localStorage.getItem(WIDTH_STORAGE_KEY));
    if (Number.isFinite(stored) && stored > 0) return clampSidebarWidth(stored);
  } catch (error) {
    console.error("Failed to read sidebar width from localStorage:", error);
  }
  return SIDEBAR_PINNED_PX;
}

export function writeStoredSidebarWidth(width: number) {
  try {
    localStorage.setItem(WIDTH_STORAGE_KEY, String(clampSidebarWidth(width)));
  } catch (error) {
    console.error("Failed to save sidebar width to localStorage:", error);
  }
}

/**
 * Exported for the cold-load placeholder, which renders above `SidebarProvider`
 * (AuthWall shows it before the app shell mounts) and so has to size its own
 * sidebar rail from the same persisted value the provider will read.
 *
 * `true` means the panel is PINNED as a column (#12209); `false` is the rail
 * at rest, from which a hover reveals the panel without changing this value.
 * With nothing stored, the panel starts unpinned: the rail is the resting
 * state of the Claude.ai pattern this follows. A stored preference always
 * wins, so an existing user's open column stays a pinned column.
 */
export function readStoredSidebarOpen(): boolean {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "true") return true;
    if (stored === "false") return false;
  } catch (error) {
    console.error("Failed to read sidebar state from localStorage:", error);
  }
  return false;
}

export function writeStoredSidebarOpen(open: boolean) {
  try {
    localStorage.setItem(STORAGE_KEY, open ? "true" : "false");
  } catch (error) {
    console.error("Failed to save sidebar state to localStorage:", error);
  }
}

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return context;
};

/**
 * Collapse the left navigation sidebar when `panelOpen` flips from closed to
 * open. A side panel that takes its width from the content beside it — the RMS
 * filters panel, which is inline rather than an overlay — otherwise leaves the
 * content squeezed between two panels (#11233).
 *
 * Only the closed → open transition collapses. A surface that mounts with its
 * panel already open leaves the sidebar alone, so merely navigating to such a
 * view never yanks the nav shut. There is no restore on close either: the
 * sidebar stays where it is, as if the user had collapsed it by hand. On the
 * RMS search view a restore would additionally fight Layout, which collapses
 * this same sidebar when a document opens — an event that view answers by
 * auto-hiding its filters panel, so the restore would re-expand the nav
 * underneath the viewer.
 *
 * Reads the context directly rather than through `useSidebar`, which throws
 * without a provider. The grids that call this are legitimately mounted outside
 * the app shell (in isolation, in stories); "there is no sidebar here" is a
 * no-op, not an error.
 */
export const useCollapseSidebarOnPanelOpen = (panelOpen: boolean): void => {
  const context = useContext(SidebarContext);
  const setSidebarOpen = context?.setSidebarOpen;
  const panelWasOpenRef = useRef(panelOpen);
  useEffect(() => {
    const justOpened = panelOpen && !panelWasOpenRef.current;
    panelWasOpenRef.current = panelOpen;
    // Phone (#11742, review round 2): there is no nav column to collapse,
    // and writing the stored rail preference from here would surprise the
    // same user back on desktop — the same guard Layout's viewer-collapse
    // carries.
    if (justOpened && currentViewportTier() !== "phone") {
      setSidebarOpen?.(false);
    }
  }, [panelOpen, setSidebarOpen]);
};

/**
 * Whether Layout's document pane is open beside the page, read defensively:
 * outside the app shell (stories, embeds) there is no such pane, so false.
 */
export const useDocumentPaneOpen = (): boolean =>
  useContext(SidebarContext)?.documentPaneOpen ?? false;

/** The artifact Layout's pane shows (see `paneArtifact`), read defensively. */
export const usePaneArtifact = (): PaneArtifact | null =>
  useContext(SidebarContext)?.paneArtifact ?? null;

const fallbackActivation = { current: 0 };
let fallbackMark = 0;
/** The shared activation counter (see `nextActivationMark`); outside the
 *  provider a private one, so a page rendered alone still decides. */
export const usePaneActivation = (): {
  nextActivationMark: () => number;
  paneActivationRef: React.MutableRefObject<number>;
} => {
  const ctx = useContext(SidebarContext);
  return ctx
    ? {
        nextActivationMark: ctx.nextActivationMark,
        paneActivationRef: ctx.paneActivationRef,
      }
    : {
        nextActivationMark: () => ++fallbackMark,
        paneActivationRef: fallbackActivation,
      };
};

/** The pane's split, read defensively (see `paneSplit`). */
export const usePaneSplit = (): PaneSplit =>
  useContext(SidebarContext)?.paneSplit ?? RESTING_SPLIT;

/** Layout's pane split writer (see `paneSplitWriter`); null outside the
 *  app shell. */
export const usePaneSplitWriter = ():
  | ((percent: number | null) => void)
  | null => useContext(SidebarContext)?.paneSplitWriter ?? null;

/** Asks Layout for a page share while `percent` is a number (see
 *  `paneSplitRequest`); a no-op outside the app shell. */
export const usePaneSplitRequest = (percent: number | null): void => {
  const context = useContext(SidebarContext);
  const setPaneSplitRequest = context?.setPaneSplitRequest;
  useLayoutEffect(() => {
    if (!setPaneSplitRequest) return undefined;
    setPaneSplitRequest(percent);
    return () => setPaneSplitRequest(null);
  }, [percent, setPaneSplitRequest]);
};

/**
 * A ref callback for the element Layout's document pane should render into
 * (see `paneSlot`); a no-op outside the app shell.
 */
export const usePaneSlotRef = (): ((slot: HTMLElement | null) => void) => {
  const context = useContext(SidebarContext);
  const setPaneSlot = context?.setPaneSlot;
  return useCallback(
    (slot: HTMLElement | null) => setPaneSlot?.(slot),
    [setPaneSlot]
  );
};

/**
 * The phone nav drawer's state, read defensively (#11742): topbars mount on
 * surfaces that are legitimately outside the app shell (stories, embeds),
 * where "there is no drawer here" is a null, not an error.
 */
export const useMobileNavDrawer = (): {
  setOpen: (open: boolean) => void;
} | null => {
  const context = useContext(SidebarContext);
  const setOpen = context?.setMobileDrawerOpen;
  return useMemo(() => (setOpen === undefined ? null : { setOpen }), [setOpen]);
};

/**
 * Keep the hover-open panel open while `active` (#12209): for a popup the
 * panel opened whose paper lives outside the panel's DOM. Read defensively,
 * since the history mounts in stories with no provider.
 */
export const useSidebarRevealHold = (active: boolean): void => {
  const context = useContext(SidebarContext);
  const holdReveal = context?.holdReveal;
  useEffect(() => {
    if (!active || holdReveal === undefined) return;
    return holdReveal();
  }, [active, holdReveal]);
};

interface SidebarProviderProps {
  children: React.ReactNode;
}

export const SidebarProvider: React.FC<SidebarProviderProps> = ({
  children,
}) => {
  const [sidebarOpen, setSidebarOpenState] = useState<boolean>(() =>
    readStoredSidebarOpen()
  );
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [workspaceHistoryKey, setWorkspaceHistoryKey] = useState("");
  const [documentPaneOpen, setDocumentPaneOpen] = useState(false);
  const [paneSlot, setPaneSlot] = useState<HTMLElement | null>(null);
  const [paneSplit, setPaneSplit] = useState<PaneSplit>(RESTING_SPLIT);
  const [paneSplitRequest, setPaneSplitRequest] = useState<number | null>(null);
  const [paneSplitWriterState, setPaneSplitWriterState] = useState<{
    write: ((percent: number | null) => void) | null;
  }>({ write: null });
  const setPaneSplitWriter = useCallback(
    (writer: ((percent: number | null) => void) | null) =>
      setPaneSplitWriterState({ write: writer }),
    []
  );
  const paneSplitWriter = paneSplitWriterState.write;
  const [paneArtifact, setPaneArtifact] = useState<PaneArtifact | null>(null);
  const activationCounterRef = useRef(0);
  const paneActivationRef = useRef(0);
  const nextActivationMark = useCallback(() => {
    activationCounterRef.current += 1;
    return activationCounterRef.current;
  }, []);

  const setSidebarOpen = useCallback((open: boolean) => {
    writeStoredSidebarOpen(open);
    setSidebarOpenState(open);
  }, []);
  const [sidebarWidth, setSidebarWidthState] = useState<number>(() =>
    readStoredSidebarWidth()
  );
  const [sidebarResizing, setSidebarResizing] = useState(false);
  const [revealHolds, setRevealHolds] = useState(0);
  const holdReveal = useCallback(() => {
    setRevealHolds((n) => n + 1);
    return () => setRevealHolds((n) => n - 1);
  }, []);
  const setSidebarWidth = useCallback((width: number, persist = true) => {
    const clamped = clampSidebarWidth(width);
    if (persist) writeStoredSidebarWidth(clamped);
    setSidebarWidthState(clamped);
  }, []);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      sidebarOpen,
      setSidebarOpen,
      sidebarWidth,
      setSidebarWidth,
      sidebarResizing,
      setSidebarResizing,
      revealHolds,
      holdReveal,
      mobileDrawerOpen,
      setMobileDrawerOpen,
      workspaceHistoryKey,
      setWorkspaceHistoryKey,
      documentPaneOpen,
      setDocumentPaneOpen,
      paneSlot,
      setPaneSlot,
      paneSplit,
      setPaneSplit,
      paneSplitRequest,
      setPaneSplitRequest,
      paneSplitWriter,
      setPaneSplitWriter,
      paneArtifact,
      setPaneArtifact,
      nextActivationMark,
      paneActivationRef,
    }),
    [
      sidebarOpen,
      setSidebarOpen,
      sidebarWidth,
      setSidebarWidth,
      sidebarResizing,
      revealHolds,
      holdReveal,
      mobileDrawerOpen,
      workspaceHistoryKey,
      documentPaneOpen,
      paneSlot,
      paneSplit,
      paneSplitRequest,
      paneSplitWriter,
      setPaneSplitWriter,
      paneArtifact,
      nextActivationMark,
    ]
  );

  return (
    <SidebarContext.Provider value={contextValue}>
      {children}
    </SidebarContext.Provider>
  );
};
